import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import SharedHeaderContractor from './SharedHeaderContractor';
import ResponsiveContainer from './layout/ResponsiveContainer';
import { getApiUrl } from '../lib/getApiUrl';
import { NextActionBanner } from './NextActionBanner'; // CB:BEGIN next-action-banner

interface ContractorActiveProjectsProps {
  onNavigate: (screen: string) => void;
}

interface Project {
  id: string;
  address: string;
  status: string;
  projectType?: string;
  escrowAccount?: {
    totalDeposited: number;
  };
  homeowner: {
    // CB:BEGIN homeowner-name-fields
    firstName?: string;
    lastName?: string;
    user: {
      email: string;
    };
    // CB:END homeowner-name-fields
  };
  acceptedBid: {
    id: string;
    amount: number;
    totalProjectAmount?: number;
    createdAt: string;
  };
}

const ContractorActiveProjects: React.FC<ContractorActiveProjectsProps> = ({ onNavigate }) => {
  const { user } = useUser();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActiveProjects = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { getApiUrl } = await import('../lib/getApiUrl');
        const apiUrl = getApiUrl(`/api/contractors/active-projects?userId=${user.id}`);
        const response = await fetch(apiUrl, { credentials: 'include' });
        
        if (response.ok) {
          const data = await response.json();
          setProjects(data.projects || []);
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Failed to fetch projects' }));
          setError(String(errorData.error || 'Failed to load active projects'));
        }
      } catch (err) {
        console.error('Error fetching active projects:', err);
        setError('Failed to load active projects');
      } finally {
        setLoading(false);
      }
    };

    fetchActiveProjects();
  }, [user?.id]);

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <ResponsiveContainer>
      <div className="container">
        <SharedHeaderContractor onNavigate={onNavigate} />

        {/* Content */}
        <div className="content">
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 20px' }}>
            <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px', color: '#111827' }}>
              Active Projects
            </h1>
            <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '32px' }}>
              Projects you're currently working on
            </p>

            {loading ? (
              <div className="dashboard-card">
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px 20px' }}>
                  Loading active projects...
                </p>
              </div>
            ) : error ? (
              <div className="dashboard-card" style={{ border: '1px solid #dc2626', background: '#fef2f2' }}>
                <p style={{ color: '#dc2626', textAlign: 'center', padding: '40px 20px' }}>
                  {error}
                </p>
              </div>
            ) : projects.length === 0 ? (
              <div className="dashboard-card">
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px 20px' }}>
                  No active projects yet. Submit a bid on a project to get started!
                </p>
              </div>
            ) : (
              <div className="dashboard-grid">
                {projects.map((project) => {
                  // Normalize project amount to reflect what the homeowner is paying (bid + platform fee).
                  const rawTotalCents = project.acceptedBid.totalProjectAmount ?? project.acceptedBid.amount ?? 0;
                  const rawBidCents = project.acceptedBid.amount ?? 0;
                  let projectAmountCents = rawTotalCents;
                  if (rawBidCents > 0 && (rawTotalCents === 0 || rawTotalCents < rawBidCents * 1.05)) {
                    // totalProjectAmount missing or looks like just the bid → approximate bid + 10% fee
                    projectAmountCents = Math.round(rawBidCents * 1.1);
                  }

                  // Active projects view should treat awarded projects as ACTIVE, unless explicitly cancelled/disputed.
                  const normalizedStatus =
                    project.status === 'CANCELLED' || project.status === 'DISPUTED'
                      ? project.status
                      : 'ACTIVE';

                  return (
                  <div
                    key={project.id}
                    className="dashboard-card"
                    style={{
                      border: '2px solid #10b981',
                      backgroundColor: '#f0fdf4',
                      cursor: 'pointer'
                    }}
                  >
                    {/* CB:BEGIN next-action-banner-contractor */}
                    <NextActionBanner
                      projectId={project.id}
                      forRole="contractor"
                      compact
                    />
                    {/* CB:END next-action-banner-contractor */}
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12, cursor: 'pointer' }}
                      onClick={() => onNavigate(`/contractor/projects/${project.id}`)}
                    >
                      <div>
                        <h4 style={{ margin: '0 0 8px 0' }}>
                          {(project.projectType || 'Roof')} Replacement
                        </h4>
                        <p style={{ color: '#6b7280', margin: '0 0 4px 0' }}>{project.address}</p>
                        {/* CB:BEGIN homeowner-name-display */}
                        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
                          Homeowner: {project.homeowner.firstName && project.homeowner.lastName
                            ? `${project.homeowner.firstName} ${project.homeowner.lastName}`
                            : project.homeowner.user.email}
                        </p>
                        {/* CB:END homeowner-name-display */}
                      </div>
                      <span className="status-badge status-success">Accepted</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 6, columnGap: 16, marginTop: 12 }}>
                      <div style={{ color: '#374151' }}>Status</div>
                      <div style={{ color: '#374151', textAlign: 'right' }}>Project Amount</div>
                      <div style={{ color: '#6b7280' }}>
                        {normalizedStatus}
                      </div>
                      <div style={{ color: '#10b981', fontWeight: 600, textAlign: 'right' }}>
                        {formatPrice(projectAmountCents)}
                      </div>
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                      <p style={{ color: '#6b7280', fontSize: 12, margin: 0 }}>
                        Accepted: {formatDate(project.acceptedBid.createdAt)}
                      </p>
                    </div>
                  </div>
                  );
                })}
            </div>
            )}
          </div>
        </div>
      </div>
    </ResponsiveContainer>
  );
};

export default ContractorActiveProjects;

